import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { SEOHead } from '../components/common/SEOHead';
import { Breadcrumbs } from '../components/common/Breadcrumbs';
import { ProductCard } from '../components/common/ProductCard';
import { LeadFormDialog } from '../components/common/LeadFormDialog';
import { getBrand, getProducts, getCategories } from '../lib/api';
import { generateWhatsAppLink } from '../lib/utils';
import { MessageCircle } from 'lucide-react';

export default function BrandCatalogPage() {
  const { brandSlug } = useParams();
  const [brand, setBrand] = useState(null);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [loading, setLoading] = useState(true);
  const [showQuoteForm, setShowQuoteForm] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setSelectedCategory('all');
      try {
        const brandRes = await getBrand(brandSlug);
        setBrand(brandRes.data);

        const [prodRes, catsRes] = await Promise.all([
          getProducts({ brand_id: brandRes.data.id, limit: 5000 }),
          getCategories(),
        ]);
        setProducts(prodRes.data || []);
        setCategories(catsRes.data || []);
      } catch (error) {
        console.error('Error fetching brand catalog:', error);
        setBrand(null);
        setProducts([]);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [brandSlug]);

  const categoriesInBrand = useMemo(() => {
    const ids = new Set(products.map((p) => p.category_id).filter(Boolean));
    return categories.filter((c) => ids.has(c.id));
  }, [products, categories]);

  const filteredProducts = useMemo(() => {
    if (selectedCategory === 'all') return products;
    return products.filter((p) => p.category_id === selectedCategory);
  }, [products, selectedCategory]);

  if (loading) {
    return (
      <div className="pt-32 min-h-screen bg-[#F4F4F5]">
        <div className="container-custom">
          <div className="skeleton h-48 mb-8" />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="skeleton h-72" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!brand) {
    return (
      <div className="pt-32 min-h-screen">
        <div className="container-custom text-center py-20">
          <h1 className="heading-lg mb-4">Brand Not Found</h1>
          <Link to="/products" className="btn-primary">View All Products</Link>
        </div>
      </div>
    );
  }

  const whatsappMessage = `Hi, I'm interested in ${brand.name} products. Please share the catalog and wholesale pricing.`;
  const whatsappLink = generateWhatsAppLink(whatsappMessage);

  return (
    <>
      <SEOHead
        title={`${brand.name} - Wholesale Fragrance Brand`}
        description={
          brand.description ||
          `Shop authentic ${brand.name} products at wholesale prices. Silver Perfumes - your trusted fragrance wholesaler.`
        }
        canonical={`https://silverperfumes.thevinculum.org.in/brand/${brand.slug}`}
        ogImage={brand.logo}
      />

      <section className="pt-32 pb-16 bg-[#0A0A0A]" data-testid="brand-catalog-hero">
        <div className="container-custom">
          <Breadcrumbs
            items={[
              { label: 'Products', href: '/products' },
              { label: brand.name },
            ]}
          />
          <div className="mt-6 max-w-2xl">
            <span className="label-text mb-4 block">Brand</span>
            {brand.logo ? (
              <div className="mb-6 p-4 bg-white/5 inline-block">
                <img
                  src={brand.logo}
                  alt={brand.name}
                  className="h-16 w-auto object-contain"
                />
              </div>
            ) : null}
            <h1 className="heading-xl text-white mb-4">{brand.name}</h1>
            {brand.description && (
              <p className="text-gray-300 text-lg mb-6">{brand.description}</p>
            )}
            <div className="flex flex-wrap gap-4">
              <button
                onClick={() => setShowQuoteForm(true)}
                className="btn-silver"
                data-testid="brand-catalog-quote-btn"
              >
                Request Wholesale Quote
              </button>
              <a
                href={whatsappLink}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-secondary border-white text-white hover:bg-white hover:text-[#0A0A0A] flex items-center gap-2"
                data-testid="brand-catalog-whatsapp-btn"
              >
                <MessageCircle className="h-4 w-4" />
                WhatsApp Enquiry
              </a>
            </div>
          </div>
        </div>
      </section>

      {categoriesInBrand.length > 0 && (
        <section className="py-8 bg-white border-b border-gray-100" data-testid="brand-catalog-categories">
          <div className="container-custom">
            <div className="flex flex-wrap gap-2 items-center">
              <span className="text-xs uppercase tracking-widest text-gray-500 mr-2">Filter by category</span>
              <button
                type="button"
                onClick={() => setSelectedCategory('all')}
                className={`px-4 py-2 text-xs uppercase tracking-wider border transition-colors ${
                  selectedCategory === 'all'
                    ? 'bg-[#0A0A0A] text-white border-[#0A0A0A]'
                    : 'bg-white text-gray-600 border-gray-200 hover:border-[#C0C4CC]'
                }`}
              >
                All
              </button>
              {categoriesInBrand.map((cat) => (
                <button
                  key={cat.id}
                  type="button"
                  onClick={() => setSelectedCategory(cat.id)}
                  className={`px-4 py-2 text-xs uppercase tracking-wider border transition-colors ${
                    selectedCategory === cat.id
                      ? 'bg-[#0A0A0A] text-white border-[#0A0A0A]'
                      : 'bg-white text-gray-600 border-gray-200 hover:border-[#C0C4CC]'
                  }`}
                >
                  {cat.name}
                </button>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="py-16 bg-[#F4F4F5]" data-testid="brand-catalog-products">
        <div className="container-custom">
          <div className="flex justify-between items-center mb-8">
            <h2 className="heading-md">{brand.name} Products</h2>
            <span className="text-gray-500">{filteredProducts.length} products</span>
          </div>

          {filteredProducts.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {filteredProducts.map((product) => {
                const category = categories.find((c) => c.id === product.category_id);
                return (
                  <ProductCard
                    key={product.id}
                    product={product}
                    category={category?.name}
                    categorySlug={category?.slug}
                    brand={brand.name}
                    brandSlug={brand.slug}
                  />
                );
              })}
            </div>
          ) : (
            <div className="text-center py-12 bg-white">
              <p className="text-gray-500 mb-4">No products found for this brand yet.</p>
              <button onClick={() => setShowQuoteForm(true)} className="btn-primary">
                Request Quote for {brand.name}
              </button>
            </div>
          )}
        </div>
      </section>

      <section className="py-16 bg-white" data-testid="brand-catalog-cta">
        <div className="container-custom text-center">
          <h2 className="heading-md mb-4">Need More {brand.name} Options?</h2>
          <p className="text-gray-600 mb-8 max-w-xl mx-auto">
            Contact us for the full {brand.name} wholesale range and pricing.
          </p>
          <button
            onClick={() => setShowQuoteForm(true)}
            className="btn-primary"
          >
            Request Custom Quote
          </button>
        </div>
      </section>

      <LeadFormDialog
        isOpen={showQuoteForm}
        onClose={() => setShowQuoteForm(false)}
        sourcePageUrl={`/brand/${brand.slug}`}
        interestBrandId={brand.id}
        showBuyerType={true}
        title={`Request ${brand.name} Quote`}
        subtitle={`Get wholesale pricing for our ${brand.name} collection`}
      />
    </>
  );
}
